/**
 * Нижняя навигация телефона (план 110) — замена сайдбару на узких экранах.
 *
 * ПОЧЕМУ ВНИЗУ. Сайдбар в 256px съедает четверть экрана телефона, а его пункты
 * недостижимы большим пальцем. Нижняя панель — то, к чему приучены все
 * мобильные приложения, и она попадает в зону пальца.
 *
 * ПЯТЬ МЕСТ. Четыре ежедневных раздела + «Ещё» со всем остальным. Состав
 * основной четвёрки — не вкусовщина: расписание и мессенджер администратор
 * открывает десятки раз за смену, контакты — при каждом звонке, дашборд —
 * точка входа. Всё прочее живёт в «Ещё» одним списком.
 *
 * ПРАВА. Пункты фильтруются тем же `can()`, что и сайдбар, и берутся из ОДНОГО
 * списка `NAV_ITEMS` (см. Layout): два меню с разными наборами прав разъехались
 * бы при первой же правке RBAC. Пункт, закрытый правом, из нижней панели
 * выпадает, а его место занимает следующий — дыр в панели не бывает.
 */
import { MoreHorizontal, X, type LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { cn } from '@/lib/utils';

export interface MobileNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Бейдж непрочитанных (мессенджер). */
  badge?: number;
}

interface Props {
  /** Уже отфильтрованные по правам пункты — в порядке сайдбара. */
  items: readonly MobileNavItem[];
  /** Адреса основной четвёрки; порядок задаёт порядок вкладок. */
  primary: readonly string[];
  /** Подвал листа «Ещё»: имя, роль, выход. */
  footer?: React.ReactNode;
}

export function MobileNav({ items, primary, footer }: Props): JSX.Element {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  // Переход по ссылке закрывает лист «Ещё»: иначе он остаётся поверх раздела,
  // в который человек только что перешёл.
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Блокируем прокрутку страницы под открытым листом — иначе палец скроллит
  // содержимое раздела, а не список.
  useEffect(() => {
    if (!moreOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [moreOpen]);

  const tabs = primary
    .map((to) => items.find((i) => i.to === to))
    .filter((i): i is MobileNavItem => Boolean(i));
  // В «Ещё» — всё, чего нет во вкладках (включая пункты, не попавшие в primary
  // из-за прав: список уже отфильтрован).
  const rest = items.filter((i) => !tabs.some((t) => t.to === i.to));
  const moreActive = rest.some((i) => location.pathname.startsWith(i.to));

  return (
    <>
      {/* Лист «Ещё» — полноэкранный, чтобы пункты были крупными и попадаемыми. */}
      {moreOpen ? (
        <div className="fixed inset-0 z-40 flex flex-col bg-canvas-white pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] lg:hidden">
          <div className="flex items-center justify-between border-b border-divider-gray px-4 py-3">
            <span className="text-lg font-semibold text-ink-black">Меню</span>
            <button
              type="button"
              onClick={() => setMoreOpen(false)}
              aria-label="Закрыть меню"
              className="flex h-11 w-11 items-center justify-center rounded-full text-muted-text hover:bg-whisper-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="min-h-0 flex-1 overflow-y-auto p-2">
            {rest.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      // min-h-[52px] — тач-таргет с запасом: пункты меню жмут
                      // на ходу, промах стоит лишнего перехода.
                      'flex min-h-[52px] items-center gap-3 rounded-2xl px-4 text-base',
                      isActive
                        ? 'bg-blueprint-blue text-canvas-white'
                        : 'text-ink-black hover:bg-whisper-white',
                    )
                  }
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {item.badge ? (
                    <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-soft-orange px-1.5 text-[11px] font-semibold text-canvas-white">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </nav>
          {footer ? <div className="border-t border-divider-gray p-4">{footer}</div> : null}
        </div>
      ) : null}

      {/* Сама панель. pb-[env(safe-area-inset-bottom)] — под «домашнюю» полосу
          iPhone, иначе нижний ряд кнопок наполовину под ней. */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-divider-gray bg-canvas-white pb-[env(safe-area-inset-bottom)] lg:hidden"
        aria-label="Основная навигация"
      >
        {tabs.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'relative flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[11px]',
                  isActive && !moreOpen ? 'text-blueprint-blue' : 'text-muted-text',
                )
              }
            >
              <span className="relative">
                <Icon className="h-5 w-5" />
                {item.badge ? (
                  <span className="absolute -right-2.5 -top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-soft-orange px-1 text-[10px] font-semibold text-canvas-white">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                ) : null}
              </span>
              <span className="max-w-full truncate">{item.label}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          aria-label="Ещё"
          aria-expanded={moreOpen}
          className={cn(
            'flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[11px]',
            moreOpen || moreActive ? 'text-blueprint-blue' : 'text-muted-text',
          )}
        >
          <MoreHorizontal className="h-5 w-5" />
          <span>Ещё</span>
        </button>
      </nav>
    </>
  );
}
